import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router';
import { OTP_LENGTH } from '@mys/contracts/constants';
import { ApiError } from '../lib/api.js';
import { useSession } from '../lib/session.js';

/* ============================================================================
   /login — phone, then the code.
   ----------------------------------------------------------------------------
   Two steps in one screen rather than two routes, so the back button does not
   land someone between them and so the number stays on screen while they type
   the code that was sent to it.

   There is no password here and there never will be. These are shop owners we
   already talk to on WhatsApp; a password is one more thing to forget and one
   more thing for us to reset over the phone, which is the weakest link in any
   account recovery.
   ========================================================================== */

type Step = 'phone' | 'code';

export function LoginPage() {
  const { status, requestCode, verifyCode } = useSession();
  const location = useLocation();

  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Seconds until another code can be asked for. Counted down from the
      server's own figure, never guessed at by the browser. */
  const [cooldown, setCooldown] = useState(0);

  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  useEffect(() => {
    if (step === 'code') codeRef.current?.focus();
  }, [step]);

  if (status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />;
  }

  async function askForCode(event?: FormEvent) {
    event?.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await requestCode(phone);
      /* The message is the API's, unchanged. It says "agar yeh number
         registered hai" rather than "code bhej diya", because the API
         deliberately does not reveal whether the number is one of ours — see
         CONTEXT.md §12. Rewording it here to something more confident would
         undo that on the only screen anybody reads. */
      setNotice(result.message);
      setCooldown(result.retryAfterSeconds);
      setStep('code');
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Code nahi bhej paye.');
      /* A 429 carries its own wait in the message; keep the user on this step
         so they can read it rather than staring at an empty code box. */
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await verifyCode(phone, code);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Code check nahi ho paya.');
      setCode('');
      codeRef.current?.focus();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <div className="login__card">
        <h1 className="login__title">Aapka project</h1>
        <p className="muted">
          {step === 'phone'
            ? 'Wahi WhatsApp number daaliye jo humein diya tha.'
            : `${phone} par bheja gaya code daaliye.`}
        </p>

        {step === 'phone' ? (
          <form onSubmit={askForCode} noValidate>
            <div className="field">
              <label htmlFor="phone">WhatsApp number</label>
              <input
                id="phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel"
                placeholder="98765 43210"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
              />
            </div>
            <button className="btn btn--block" type="submit" disabled={busy || phone.trim() === ''}>
              {busy ? 'Bhej rahe hain…' : 'Code bhejiye'}
            </button>
          </form>
        ) : (
          <form onSubmit={submitCode} noValidate>
            <div className="field">
              <label htmlFor="code">Code</label>
              <input
                id="code"
                ref={codeRef}
                type="text"
                inputMode="numeric"
                /* The one-time-code hint is what makes Android and iOS offer to
                   fill it straight from the notification, which on this
                   audience's phones is the difference between one tap and
                   switching apps to read it. */
                autoComplete="one-time-code"
                maxLength={OTP_LENGTH + 2}
                placeholder={'0'.repeat(OTP_LENGTH)}
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
            </div>

            <button className="btn btn--block" type="submit" disabled={busy || code.trim() === ''}>
              {busy ? 'Check kar rahe hain…' : 'Aage badhiye'}
            </button>

            <div className="login__resend">
              <button
                type="button"
                className="btn btn--ghost btn--small"
                disabled={busy || cooldown > 0}
                onClick={() => void askForCode()}
              >
                {cooldown > 0 ? `Naya code ${cooldown} second mein` : 'Naya code bhejiye'}
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--small"
                onClick={() => {
                  setStep('phone');
                  setCode('');
                  setError(null);
                  setNotice(null);
                }}
              >
                Number badliye
              </button>
            </div>
          </form>
        )}

        {error ? (
          <p className="alert" role="alert">
            {error}
          </p>
        ) : null}

        {notice && !error ? (
          <p className="muted login__notice" role="status">
            {notice}
          </p>
        ) : null}

        <p className="muted login__help">
          Number badal gaya hai ya code nahi aa raha?{' '}
          <a href="https://wa.me/918630415226">WhatsApp par humein message kijiye</a>.
        </p>
      </div>
    </div>
  );
}
